import React from 'react';
import { Alert } from '@mui/material';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Button, Input } from '../../../ui';
import { ErrorIcon } from '../../../ui/icons';
import ReCaptchaV3 from '../../../Wrappers/ReCaptchaV3';
import { DISABLE_CAPTCHA } from '../../../../utils';
import { assetUrl } from '../../../../utils/webConfig';
import type { LoginFormValues } from './useLoginStep';

type AuthLoginStepViewProps = {
    register: UseFormRegister<LoginFormValues>;
    errors: FieldErrors<LoginFormValues>;
    loading?: boolean;
    email: string;
    showPassword: boolean;
    recoverySuccess?: boolean;
    error?: string | null;
    dismissError?: () => void;
    onToggleShowPassword?: () => void;
    onForgotPassword: () => void;
    onSubmit?: React.FormEventHandler<HTMLFormElement>;
};

export const AuthLoginStepView: React.FC<AuthLoginStepViewProps> = ({
    register,
    errors,
    loading,
    email,
    showPassword,
    recoverySuccess,
    error,
    dismissError,
    onToggleShowPassword,
    onForgotPassword,
    onSubmit,
}) => (
    <form id="auth-login-form" onSubmit={onSubmit}>
        <div
            id="auth-login-step"
            className="flex flex-col mt-8 w-full min-h-[412px]"
            data-testid="login-step"
        >
            {recoverySuccess && (
                <Alert
                    id="auth-login-recovery-success-alert"
                    className="mb-4"
                    severity="success"
                    data-testid="recovery-success-alert"
                >
                    We emailed you the reset link.
                </Alert>
            )}
            <div
                id="auth-login-welcome-message"
                className="text-body-small text-secondary mb-1"
            >
                We remember you!
            </div>
            <div
                id="auth-login-heading"
                className="text-body-large text-primary mb-6"
            >
                Let&apos;s get you signed in
            </div>
            <Input
                id="auth-login-email"
                label="Email Address"
                value={email}
                disabled
                className="mb-3"
                readOnly
            />
            <div id="auth-login-password-container" className="relative mb-3">
                <style id="auth-login-password-visibility-styles">{`
                    input[type="password"]::-ms-reveal,
                    input[type="password"]::-ms-clear,
                    input[type="password"]::-webkit-contacts-auto-fill-button,
                    input[type="password"]::-webkit-credentials-auto-fill-button { display: none; }
                `}</style>
                <Input
                    {...register('password', {
                        required: '*Password is required',
                    })}
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    id="auth-login-password"
                />
                <button
                    id="auth-login-password-visibility-button"
                    type="button"
                    className="absolute right-3 top-[50%] translate-y-[-50%] text-secondary border-none bg-transparent"
                    onClick={onToggleShowPassword}
                    tabIndex={-1}
                >
                    <img
                        id="auth-login-password-visibility-icon"
                        src={assetUrl(
                            `/images/svg/${showPassword ? 'pass_hide' : 'pass_show'}.svg`,
                        )}
                        alt={showPassword ? 'Hide password' : 'Show password'}
                        width={17}
                        height={17}
                    />
                </button>
            </div>
            <Button
                id="auth-login-forgot-password-button"
                type="button"
                variant="link"
                className="text-body-small text-accent self-start"
                onClick={onForgotPassword}
                data-testid="forgot-password-btn"
            >
                I forgot my password
            </Button>
            {error ? (
                <Alert
                    id="auth-login-error-alert"
                    className="mt-4"
                    severity="error"
                    onClose={dismissError}
                    iconMapping={{ error: <ErrorIcon /> }}
                >
                    {error}
                </Alert>
            ) : null}
            {!DISABLE_CAPTCHA && <ReCaptchaV3 hideText={true} />}
            <Button
                id="auth-login-continue-button"
                type="submit"
                className="w-full mt-auto"
                loading={loading}
            >
                Continue
            </Button>
        </div>
    </form>
);
